import React, { useEffect, useRef, useState, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  Button,
  Typography,
  Breadcrumb,
  Space,
  Spin,
  Modal,
  Input,
  Row,
  Col,
  notification,
  Result,
  DatePicker,
  Radio,
  Form,
} from "antd";
import { UserOutlined, Loading3QuartersOutlined, FormOutlined } from "@ant-design/icons";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment-timezone";
import metaData from "../../../data/metaData";
import _ from "lodash";

//
import RequestDetailView from "../../../components/ProductRequest/View";
import RequestMeta from "../../../components/RequestMeta";

//
import useAuthStore from "../../../states/useAuth";
import useRequest from "../../../hooks/useRequest";
import PageHeader from "../../../components/PageHeader";
import Status from "../../../components/Table/RequestStatus";
import useRequestStore from "../../../states/useRequest";
import deepcopy from "deepcopy";
import { showErrMsg } from "../../../utils/error";
import { useForm } from "antd/es/form/Form";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const SingleProductRequest = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams(); // Get the :id parameter from the URL
  const { resetRequest } = useRequestStore();
  const { getSingleProductRequest, updateProductRequest } = useRequest();

  //Sider
  const [collapsed, setCollapsed] = useState(false);

  const [productForm] = Form.useForm();

  const [isShowRemarkModal, setIsShowRemarkModal] = useState(false);
  const [request, setRequest] = useState();
  const [isFetchingSingleRequest, setIsFetchingSingleRequest] = useState(false);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [isShowErrorPage, setIsShowErrorPage] = useState(false);

  const makeDecision = async ({ action, remark }) => {
    log.debug({ action, remark });

    // axios
    try {
      setIsLoadingUpdate(true);
      let updated = await updateProductRequest(request.id, { status: action, remark }, false);
      setIsLoadingUpdate(false);
      log.debug(updated);

      //update data
      setRequest({ ...request, approverId: updated.approverId, time: updated.time, status: updated.status });

      //
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Success
          </Title>
        ),
        // description: <Text strong></Text>,
      });
    } catch (error) {
      showErrMsg(error);
    }
  };

  const getSingleRequestData = async () => {
    setIsFetchingSingleRequest(true);

    //setActiveTab
    let queryURL = location.search || undefined;
    log.debug(queryURL);
    // const queryParams = c.parseQueryToJSON(queryURL);
    // const { tab } = queryParams;
    // log.debug(tab);
    // setActiveTab(tab);

    //
    let result;

    try {
      result = await getSingleProductRequest(id, false);
      log.debug(result);

      setRequest(result);
    } catch (error) {
      setIsShowErrorPage(true);
      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    } finally {
      setIsFetchingSingleRequest(false);
    }
  };

  // Function to handle updating both the form and state
  const updateProductData = (newData) => {
    //do nothing
  };

  const registerOnRefreshEvent = () => {
    const handleBeforeUnload = () => {
      resetRequest(); // Reset the state when the page is refreshed or closed
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload); // Properly remove the listener
    };
  };

  useEffect(() => {
    return registerOnRefreshEvent(); // Cleanup when component unmounts
  }, []);

  useEffect(() => {
    getSingleRequestData();
  }, [id]);

  return (
    <div
      style={{
        margin: "0 auto",
        // background: "var(--ion-color-step-50)",
      }}
    >
      <Spin
        spinning={isFetchingSingleRequest}
        // percent="auto"
        indicator={<Loading3QuartersOutlined spin style={{ color: "white", fontSize: "2rem" }} />}
        fullscreen
      />

      {isShowErrorPage ? (
        <Result
          status="404"
          title="404"
          subTitle="Sorry, the page you visited does not exist."
          extra={
            <Button
              type="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              Refresh
            </Button>
          }
        />
      ) : (
        <>
          <Breadcrumb
            items={[
              {
                title: (
                  <a
                    onClick={() => {
                      history.push("/approver/product/request");
                    }}
                  >
                    Product List
                  </a>
                ),
              },
              {
                title: <a>Product</a>,
              },
            ]}
          />

          <PageHeader
            title={<Space>Request Detail {request ? <Status status={request.status} /> : null}</Space>}
            isSticky
            actionGroup={
              <Space>
                {request?.status && request?.status != "approved" ? (
                  <Button
                    size="large"
                    color="danger"
                    variant="solid"
                    loading={isLoadingUpdate}
                    style={{ marginRight: "8px" }}
                    onClick={async () => {
                      // let data = await parseFormData();
                      setIsShowRemarkModal(true);
                    }}
                  >
                    <FormOutlined />
                    Needs Revision
                  </Button>
                ) : null}

                {request?.status && request?.status == "pending" ? (
                  <Button
                    size="large"
                    type="primary"
                    loading={isLoadingUpdate}
                    style={{ marginRight: "8px" }}
                    onClick={async () => {
                      makeDecision({ action: "approved" });
                    }}
                  >
                    Approve
                  </Button>
                ) : null}
              </Space>
            }
            onBack={() => {
              history.goBack();
            }}
          />

          <RevisionModal
            isShowRemarkModal={isShowRemarkModal}
            onCancel={() => {
              setIsShowRemarkModal(false);
            }}
            onSubmit={(value) => {
              makeDecision({ action: "needRevision", remark: value });
              setIsShowRemarkModal(false);
            }}
          />

          {isFetchingSingleRequest ? null : (
            <>
              {request ? <RequestMeta request={request} /> : null}

              <Row
                style={{
                  background: "var(--ion-color-step-50)",
                  paddingTop: "2rem",
                }}
              >
                <RequestDetailView
                  state={{
                    data: request,
                    form: productForm,
                    onChange: updateProductData,
                  }}
                />
              </Row>
            </>
          )}
        </>
      )}
    </div>
  );
};

const RevisionModal = ({ isShowRemarkModal, onCancel, onSubmit }) => {
  const [revisionRemark, setRevisionRemark] = useState();
  return (
    <Modal
      title="Request Revision"
      open={isShowRemarkModal}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          onClick={() => {
            let remark = deepcopy(revisionRemark);
            remark = remark || undefined;
            setRevisionRemark();
            onSubmit(remark);
          }}
        >
          Submit
        </Button>,
      ]}
    >
      <TextArea
        rows={4}
        value={revisionRemark}
        onChange={(e) => setRevisionRemark(e.target.value)}
        placeholder="Enter your revision remarks here"
      />
    </Modal>
  );
};

export default SingleProductRequest;
